import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLInlineText = () => {
    const blockVsInlineCode = `<!-- Block element (takes full width, starts new line) --&gt;
<p>This is a paragraph. It's a block element.</p>
<p>This is another paragraph on a new line.</p>

<!-- Inline elements (flow within text) --&gt;
<p>This paragraph contains <strong>bold text</strong> and <em>italic text</em> inline.</p>`;

    const spanUsageCode = `<!-- Styling specific words --&gt;
<p>The price is <span class="highlight">$99.99</span> today only!</p>

<!-- Multiple spans with different styles --&gt;
<p>
    Status: <span class="status-badge success">Active</span>
    | Balance: <span class="amount">$1,234.56</span>
</p>

<!-- Wrapping for JavaScript manipulation --&gt;
<p>Click count: <span id="counter">0</span></p>

<!-- With inline styles (avoid in production) --&gt;
<p>This word is <span style="color: red; font-weight: bold;">important</span>.</p>`;

    const strongExampleCode = `<p><strong>Warning:</strong> This operation cannot be undone.</p>
<p>The <strong>most critical</strong> step is data backup.</p>

<!-- Can be nested for even stronger emphasis --&gt;
<p><strong>URGENT: <strong>Read this immediately!</strong></strong></p>`;

    const emExampleCode = `<p>I <em>love</em> HTML! (emphasizing the feeling)</p>
<p>I love <em>HTML</em>! (emphasizing what you love)</p>

<!-- Different em placements change meaning --&gt;
<p><em>You</em> should do it. (not someone else)</p>
<p>You <em>should</em> do it. (it's important)</p>
<p>You should <em>do</em> it. (take action)</p>`;

    const markExampleCode = `<!-- Search result highlighting --&gt;
<p>Search results for "HTML": <mark>HTML</mark> is a markup language...</p>

<!-- Highlighting relevant text --&gt;
<p>Most browsers support <mark>CSS Grid</mark> and <mark>Flexbox</mark> today.</p>

<!-- Can be styled with CSS --&gt;
<mark class="pink-highlight">Important note</mark>

<style>
.pink-highlight {
    background-color: #ffb3d9;
    padding: 2px 4px;
    border-radius: 3px;
}
</style>`;

    const smallExampleCode = `<p>Price: $99.99 <small>(excludes taxes and shipping)</small></p>

<footer>
    <p>&copy; 2025 Company Name. All rights reserved.</p>
    <p><small>Terms and conditions apply. See website for details.</small></p>
</footer>

<!-- Document meta info --&gt;
<p>
    Published on January 15, 2025 
    <small>(Last updated: January 20, 2025)</small>
</p>`;

    const correctNestingCode = `<!-- Strong + Em (bold italic) --&gt;
<p>This is <strong><em>really important and emphasized</em></strong>!</p>

<!-- Link with emphasis --&gt;
<p>Read the <a href="/docs"><em>complete documentation</em></a> for details.</p>

<!-- Mark with strong --&gt;
<p>Found in search: <mark><strong>Critical security update</strong></mark></p>

<!-- Code with emphasis --&gt;
<p>The <code><em>async</em></code> keyword makes functions asynchronous.</p>`;

    const incorrectNestingCode = `<!-- Wrong: Tags overlap instead of properly nesting --&gt;
<p>This is <strong><em>wrong nesting</strong></em>!</p>
<!-- Should close em before closing strong --&gt;

<!-- Correct version: --&gt;
<p>This is <strong><em>correct nesting</em></strong>!</p>`;

    const formErrorExampleCode = `<label for="email">Email:</label>
<input type="email" id="email" required>
<span class="error-message" style="color: red; font-size: 0.9em;">
    <strong>Error:</strong> Please enter a valid email address.
</span>`;

    const statusBadgeExampleCode = `<p>
    Order Status: <span class="badge badge-success">Shipped</span>
</p>

<style>
.badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.875em;
    font-weight: 600;
    border-radius: 0.25rem;
    color: white;
}
.badge-success {
    background-color: #28a745;
}
</style>`;

    const syntaxHighlightExampleCode = `<p>
    To declare a variable, use: 
    <code><span class="keyword">const</span> <span class="variable">name</span> = <span class="string">"value"</span>;</code>
</p>

<style>
.keyword { color: #d73a49; }
.variable { color: #6f42c1; }
.string { color: #032f62; }
</style>`;

    const pricingExampleCode = `<p class="pricing">
    <span class="currency">$</span><span class="amount">99</span><span class="cents">.99</span>
    <span class="period">/month</span>
</p>

<style>
.pricing {
    font-size: 2rem;
}
.currency {
    font-size: 0.6em;
    vertical-align: super;
}
.cents {
    font-size: 0.6em;
}
.period {
    font-size: 0.5em;
    color: #666;
}
</style>`;

    const kbdExampleCode = `<p>
    Press <kbd>Ctrl</kbd> + <kbd>C</kbd> to copy, 
    <kbd>Ctrl</kbd> + <kbd>V</kbd> to paste.
</p>

<style>
kbd {
    display: inline-block;
    padding: 3px 5px;
    font: 11px monospace;
    line-height: 10px;
    color: #444d56;
    vertical-align: middle;
    background-color: #fafbfc;
    border: solid 1px #d1d5da;
    border-bottom-color: #c6cbd1;
    border-radius: 3px;
    box-shadow: inset 0 -1px 0 #c6cbd1;
}
</style>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Content & Typography</div>
                <h1>Inline Text Elements (span, strong, em)</h1>
                <p className="hero-lead">
                    Master inline elements for precise text styling and semantic markup. Learn when to use span, strong, em, and other inline elements effectively.
                </p>
            </div>

            <div className="content-section">
                <h2>Block vs. Inline Elements</h2>
                <p>
                    Understanding the difference between block and inline elements is fundamental to HTML structure.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Block Elements</th>
                                <th>Inline Elements</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Start on a new line</td>
                                <td>Flow within text</td>
                            </tr>
                            <tr>
                                <td>Take full width available</td>
                                <td>Only take up necessary width</td>
                            </tr>
                            <tr>
                                <td>Can contain block or inline elements</td>
                                <td>Can only contain inline elements</td>
                            </tr>
                            <tr>
                                <td>Examples: <code>&lt;div&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;h1&gt;</code></td>
                                <td>Examples: <code>&lt;span&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;strong&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Block vs. Inline Example</span>
                    </div>
                    <CodeBlock language="html" code={blockVsInlineCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;span&gt; Element: Generic Inline Container</h2>
                <p>
                    <code>&lt;span&gt;</code> is the inline equivalent of <code>&lt;div&gt;</code>.
                    It's a generic container with no semantic meaning, used for styling specific text portions.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Span Usage Examples</span>
                    </div>
                    <CodeBlock language="html" code={spanUsageCode} />
                </div>

                <div className="tip-box">
                    <strong> When to Use Span:</strong>
                    <ul>
                        <li>Styling part of a text without semantic meaning</li>
                        <li>Targeting text with CSS or JavaScript</li>
                        <li>When no other semantic inline element fits</li>
                    </ul>
                </div>

                <Warning><strong> Don't Overuse Span:</strong> If there's a semantic element that conveys meaning
                    (<code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;mark&gt;</code>, etc.), use that instead!</Warning>
            </div>

            <div className="content-section">
                <h2>Semantic Inline Elements Detailed</h2>

                <div className="element-showcase">
                    <div className="element-card">
                        <h3><code>&lt;strong&gt;</code> - Strong Importance</h3>
                        <p><strong>Semantic meaning:</strong> Indicates strong importance, seriousness, or urgency</p>
                        <p><strong>Default rendering:</strong> Bold text</p>
                        <p><strong>Screen readers:</strong> May emphasize with different voice tone</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={strongExampleCode} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;em&gt;</code> - Emphasis</h3>
                        <p><strong>Semantic meaning:</strong> Stress emphasis that changes sentence meaning</p>
                        <p><strong>Default rendering:</strong> Italic text</p>
                        <p><strong>Screen readers:</strong> Uses vocal stress</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={emExampleCode} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;mark&gt;</code> - Highlight/Reference</h3>
                        <p><strong>Semantic meaning:</strong> Marked or highlighted for reference</p>
                        <p><strong>Default rendering:</strong> Yellow background</p>
                        <p><strong>Use cases:</strong> Search results, relevant passages, current selection</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={markExampleCode} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h3><code>&lt;small&gt;</code> - Fine Print</h3>
                        <p><strong>Semantic meaning:</strong> Side comments, legal disclaimers, fine print</p>
                        <p><strong>Default rendering:</strong> Smaller font size</p>
                        <p><strong>Use cases:</strong> Copyright notices, terms, disclaimers</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={smallExampleCode} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Combining Inline Elements</h2>
                <p>
                    Inline elements can be nested to combine their effects. Just ensure proper nesting order.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct Nesting</span>
                    </div>
                    <CodeBlock language="html" code={correctNestingCode} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Incorrect Nesting</span>
                    </div>
                    <CodeBlock language="html" code={incorrectNestingCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Use Cases</h2>

                <div className="use-case-card">
                    <h3>1. Form Error Messages</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={formErrorExampleCode} />
                    </div>
                </div>

                <div className="use-case-card">
                    <h3>2. Status Badges</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={statusBadgeExampleCode} />
                    </div>
                </div>

                <div className="use-case-card">
                    <h3>3. Syntax Highlighting in Documentation</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={syntaxHighlightExampleCode} />
                    </div>
                </div>

                <div className="use-case-card">
                    <h3>4. Product Pricing Display</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={pricingExampleCode} />
                    </div>
                </div>

                <div className="use-case-card">
                    <h3>5. Keyboard Shortcuts Documentation</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={kbdExampleCode} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Choosing the Right Inline Element</h2>

                <div className="decision-flowchart">
                    <h3>Decision Tree:</h3>
                    <div className="flowchart">
                        <p><strong>Does the text have semantic meaning?</strong></p>
                        <ul>
                            <li>
                                <strong>YES</strong>  Use semantic element:
                                <ul>
                                    <li>Important?  <code>&lt;strong&gt;</code></li>
                                    <li>Emphasized?  <code>&lt;em&gt;</code></li>
                                    <li>Highlighted?  <code>&lt;mark&gt;</code></li>
                                    <li>Fine print?  <code>&lt;small&gt;</code></li>
                                    <li>Code?  <code>&lt;code&gt;</code></li>
                                    <li>Keyboard input?  <code>&lt;kbd&gt;</code></li>
                                    <li>Abbreviation?  <code>&lt;abbr&gt;</code></li>
                                </ul>
                            </li>
                            <li>
                                <strong>NO</strong>  Just styling?  <code>&lt;span&gt;</code> + CSS class
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use semantic elements when they convey meaning</li>
                            <li>Use <code>&lt;span&gt;</code> for purely visual styling</li>
                            <li>Nest inline elements properly</li>
                            <li>Add CSS classes for styling variations</li>
                            <li>Keep inline styling minimal (use CSS files)</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use <code>&lt;span&gt;</code> when semantic element exists</li>
                            <li>Nest block elements inside inline elements</li>
                            <li>Overuse inline styles (maintenance nightmare)</li>
                            <li>Use deprecated elements like <code>&lt;font&gt;</code></li>
                            <li>Confuse <code>&lt;strong&gt;</code>/<code>&lt;b&gt;</code> or <code>&lt;em&gt;</code>/<code>&lt;i&gt;</code></li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLInlineText;


